import { ChangeEvent, ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Button,
  Dialog,
  FormControl,
  FormHelperText,
  IconButton,
  InputProps,
  Stack,
  SvgIcon,
  SvgIconProps,
  TextField,
  Typography,
} from '@mui/material'

interface EditableFieldProps extends InputProps {
  hidePencil?: boolean
  error?: boolean
  helperText?: ReactNode
}

const EditPencilIcon = (props: SvgIconProps) => (
  <SvgIcon {...props} viewBox="0 0 74 83" sx={{ fill: 'none', ...props.sx }}>
    <path
      d="M18 56.5L18.8 45.5L46.2 18.1L55.9 27.8L28.5 55.2L18 56.5Z"
      stroke="currentColor"
      strokeWidth="4.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M39.7 24.6L49.4 34.3"
      stroke="currentColor"
      strokeWidth="4.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M18 64.5H55.5" stroke="currentColor" strokeWidth="4.8" strokeLinecap="round" />
  </SvgIcon>
)

const EditableField = ({ hidePencil, error, helperText, ...props }: EditableFieldProps) => {
  const { t } = useTranslation('common')
  const [open, setOpen] = useState(false)
  const [draftValue, setDraftValue] = useState('')

  const currentValue = props.value === undefined || props.value === null ? '' : String(props.value)

  const handleOpen = () => {
    if (hidePencil) return
    setDraftValue(currentValue)
    setOpen(true)
  }

  const handleClose = () => setOpen(false)

  const handleSave = () => {
    props.onChange?.({ target: { value: draftValue } } as ChangeEvent<HTMLInputElement>)
    setOpen(false)
  }

  return (
    <FormControl error={error}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        onClick={handleOpen}
        sx={{ cursor: hidePencil ? 'default' : 'pointer' }}
      >
        <Typography noWrap color={currentValue ? 'text.primary' : 'text.disabled'} sx={props.sx}>
          {currentValue || props.placeholder}
        </Typography>
        {!hidePencil && (
          <IconButton size="small" onClick={handleOpen} disableRipple>
            <EditPencilIcon sx={{ width: 24, height: 24, color: 'grey.500' }} />
          </IconButton>
        )}
      </Stack>
      <FormHelperText>{helperText}</FormHelperText>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700} mb={2}>
            {t('change.name')}
          </Typography>
          <Typography variant="body2" fontWeight={600} mb={0.5}>
            {t('name')}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            value={draftValue}
            placeholder={typeof props.placeholder === 'string' ? props.placeholder : undefined}
            onChange={(e) => setDraftValue(e.target.value)}
          />
          <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3}>
            <Button variant="outlined" color="inherit" onClick={handleClose}>
              {t('cancel')}
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave}>
              {t('save')}
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </FormControl>
  )
}

export default EditableField
